import { rufe } from './client';

/** Die Bereiche der Schnittstelle nach Tabelle 8 (BUC-29); `konto` seit AeA-29, `anfrage` seit 0257. */
export const BEREICHE = ['katalog', 'preis', 'reservierung', 'buchung', 'konto', 'anfrage'] as const;
export type Bereich = (typeof BEREICHE)[number];

/** Was ein Bereich öffnet — für die Maske, damit niemand raten muss. */
export const BEREICHSTEXTE: Readonly<Record<Bereich, string>> = {
  katalog: 'Produkte, Termine und Rechtstexte lesen',
  preis: 'Preise rechnen und zusagen',
  reservierung: 'Plätze halten, verlängern, freigeben',
  buchung: 'Buchen, zahlen, stornieren, umbuchen, widerrufen',
  /* AeA-29: Kunden melden sich im Formular des Portals an. */
  konto: 'Kunden anmelden, registrieren, Passwort zurücksetzen',
  /* WEB-02: Kontakt, Rückruf, Firmenanfrage, Bewerbung aus den Formularen. */
  anfrage: 'Anfragen aus den Formularen übergeben',
};

/**
 * Ein Zugang des Buchungsportals (BUC-29).
 *
 * Der Schlüssel steht hier **nicht** und kann es nicht: Die Datenbank trägt
 * nur seinen Streuwert (LEG-13). Er erscheint genau einmal — in der Antwort
 * auf das Einrichten und auf das Neusetzen.
 */
export type Portalzugang = {
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly umgebung: 'produktiv' | 'test';
  readonly umfang: readonly string[];
  readonly ratenLesend: number;
  readonly ratenSchreibend: number;
  /** BUC-30, D02: je Endkunde. */
  readonly offeneReservierungen: number;
  /** D02: Deckel je Portal. */
  readonly ratenSchreibendGesamt: number;
  readonly offeneReservierungenGesamt: number;
  readonly ereignisAdresse: string | null;
  readonly aktiv: boolean;
  readonly gueltigBis: string | null;
  readonly angelegtAm: string;
  readonly letzterAufruf: string | null;
};

export type Zugangsanlage = {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly umgebung: 'produktiv' | 'test';
  readonly umfang: readonly Bereich[];
  readonly ereignisAdresse: string | null;
};

export type Zugangsaenderung = {
  readonly bezeichnung?: string;
  readonly umfang?: readonly Bereich[];
  readonly ratenLesend?: number;
  readonly ratenSchreibend?: number;
  readonly offeneReservierungen?: number;
  readonly ratenSchreibendGesamt?: number;
  readonly offeneReservierungenGesamt?: number;
  readonly aktiv?: boolean;
  readonly gueltigBis?: string | null;
};

export async function zugaenge(): Promise<readonly Portalzugang[]> {
  const antwort = await rufe<{ zugaenge: readonly Portalzugang[] }>('/portalzugaenge');
  return antwort.zugaenge;
}

/** Richtet einen Zugang ein. Die Antwort trägt die Geheimnisse — genau einmal. */
export async function einrichten(eingabe: Zugangsanlage): Promise<{
  readonly id: string;
  readonly schluessel: string;
  readonly signaturgeheimnis: string | null;
}> {
  return rufe('/portalzugaenge', { verfahren: 'POST', rumpf: eingabe });
}

export async function schluesselNeu(id: string): Promise<{ readonly schluessel: string }> {
  return rufe(`/portalzugaenge/${id}/schluessel`, { verfahren: 'POST' });
}

export async function aendern(
  id: string,
  felder: Zugangsaenderung,
): Promise<readonly Portalzugang[]> {
  const antwort = await rufe<{ zugaenge: readonly Portalzugang[] }>(`/portalzugaenge/${id}`, {
    verfahren: 'PATCH',
    rumpf: felder,
  });
  return antwort.zugaenge;
}
